import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, DollarSign } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import './PublicChatInterface.css';

// Google icon component
const GoogleIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);


const PublicChatInterface = ({ agent, onBack, conversationId: initialConversationId, onUpdateUrl }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(initialConversationId);
  const [conversation, setConversation] = useState(null);
  const [agentAvatar, setAgentAvatar] = useState(null);
  const [userAgentAvatar, setUserAgentAvatar] = useState(null);
  const [agentPricing, setAgentPricing] = useState(null);
  const [isPaidAgent, setIsPaidAgent] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [conversationState, setConversationState] = useState(null);
  const [isRequestingEngagement, setIsRequestingEngagement] = useState(false);
  const [nextMessageCost, setNextMessageCost] = useState(0);
  const [canSendFreeMessage, setCanSendFreeMessage] = useState(true);
  const [freeMessageStatus, setFreeMessageStatus] = useState(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const { token, isAuthenticated } = useAuth();
  const { balance: walletBalance, topUpWallet, refreshWalletBalance } = useWallet();

  // Get agent avatar and pricing (for the agent being chatted with)
  useEffect(() => {
    const fetchAgentData = async () => {
      if (agent?.id) {
        try {
          const response = await fetch(`/api/agents/public`);
          if (response.ok) {
            const data = await response.json();
            const agentData = data.agents?.find(a => a.id === agent.id);
            console.log('Agent being chatted with:', agentData);
            if (agentData?.agent_avatar) {
              console.log('Setting agent avatar (B):', agentData.agent_avatar);
              setAgentAvatar(agentData.agent_avatar);
            }
            
            // Set pricing information
            if (agentData?.owner_engagement_fee_cents) {
              setAgentPricing({
                owner_engagement_fee_cents: agentData.owner_engagement_fee_cents,
                is_public: agentData.is_public
              });
              setIsPaidAgent(agentData.owner_engagement_fee_cents > 0);
            }
          }
        } catch (error) {
          console.error('Error fetching agent data:', error);
        }
      }
    };
    fetchAgentData();
  }, [agent?.id]);

  // Get user's agent avatar (for the user sending messages)
  useEffect(() => {
    const fetchUserAgentAvatar = async () => {
      if (token) {
        try {
          // Get user's agent data using the existing settings endpoint
          const response = await fetch('/api/agents/settings', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          console.log('User agent settings response status:', response.status);
          if (response.ok) {
            const data = await response.json();
            console.log('User agent settings data:', data);
            
            if (data.id) {
              // Now get the user's agent from the public agents list using the agent ID
              const publicResponse = await fetch('/api/agents/public');
              if (publicResponse.ok) {
                const publicData = await publicResponse.json();
                const userAgentData = publicData.agents?.find(a => a.id === data.id);
                console.log('User agent from public list:', userAgentData);
                
                if (userAgentData?.agent_avatar) {
                  console.log('Setting user agent avatar (A):', userAgentData.agent_avatar);
                  setUserAgentAvatar(userAgentData.agent_avatar);
                } else {
                  console.log('No agent_avatar found for user agent in public list');
                }
              } else {
                console.log('Failed to fetch public agents list');
              }
            } else {
              console.log('No agent ID in user settings');
            }
          } else {
            console.log('Failed to fetch user agent settings, status:', response.status);
          }
        } catch (error) {
          console.error('Error fetching user agent avatar:', error);
        }
      } else {
        console.log('No token available for fetching user agent avatar');
      }
    };
    fetchUserAgentAvatar();
  }, [token]);

  // Load conversation state when conversationId changes
  useEffect(() => {
    const loadConversationState = async () => {
      if (conversationId && isAuthenticated && token) {
        try {
          console.log(`🔍 [CHAT_INTERFACE] Loading conversation state for conversation ${conversationId}`);
          const response = await fetch(`/api/agents/public/conversations/${conversationId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (response.ok) {
            const conversationData = await response.json();
            console.log(`✅ [CHAT_INTERFACE] Loaded conversation state:`, conversationData);
            setConversationState(conversationData.conversation);
          }
        } catch (error) {
          console.error('Error loading conversation state:', error);
        }
      }
    };
    loadConversationState();
  }, [conversationId, agent.id, isAuthenticated, token]);

  // Prevent body scrolling when chat is active
  useEffect(() => {
    document.body.classList.add('chat-active');
    return () => {
      document.body.classList.remove('chat-active');
    };
  }, []);

  // Load conversation messages if conversationId is provided
  const loadConversationMessages = async (convId) => {
    try {
      console.log('Fetching conversation messages for ID:', convId);
      const response = await fetch(`/api/agents/public/conversations/${convId}`);
      console.log('Conversation API response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Conversation API response data:', data);
        const conversationData = data.conversation;
        if (conversationData && conversationData.messages) {
          setConversation(conversationData);
          console.log('Raw messages from server:', conversationData.messages);
          const formattedMessages = conversationData.messages.map(msg => {
            const formatted = {
              id: msg.id,
              type: msg.role === 'user' ? 'user' : 'assistant',
              content: msg.content,
              timestamp: new Date(msg.timestamp),
              isOwnerResponse: msg.is_owner_response || false
            };
            console.log('Formatted message:', formatted);
            return formatted;
          });
          console.log('Setting messages:', formattedMessages);
          setMessages(formattedMessages);
        } else {
          console.log('No messages found in conversation data');
        }
      } else {
        console.log('Failed to fetch conversation, status:', response.status);
      }
    } catch (error) {
      console.error('Error loading conversation messages:', error);
    }
  };

  // Load conversation when conversationId changes
  useEffect(() => {
    console.log('ConversationId changed:', conversationId);
    if (conversationId) {
      console.log('Loading conversation messages for:', conversationId);
      loadConversationMessages(conversationId);
    } else {
      console.log('No conversationId, clearing messages');
      setMessages([]);
    }
  }, [conversationId]);

  // Initialize messages when agent changes
  useEffect(() => {
    console.log('Agent changed:', { agent: agent?.id, conversationId });
    if (agent && !conversationId) {
      // Start with empty chat - no intro message
      console.log('Starting with empty chat for new agent');
      setMessages([]);
    }
  }, [agent]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update message cost and free message status when agent or auth changes
  useEffect(() => {
    const updateMessageInfo = async () => {
      if (isPaidAgent && isAuthenticated) {
        try {
          // Get free message status from server
          const response = await fetch(`/api/wallet/free-message-status/${agent.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const status = await response.json();
            setFreeMessageStatus(status);
            console.log('🆓 [UI_UPDATE] Free message status:', status);
          }
          
          // Check if user can send message (free or paid)
          const canSend = await canSendMessage();
          setCanSendFreeMessage(canSend);
          
          // Always get the message cost, regardless of input
          const cost = await getNextMessageCost();
          setNextMessageCost(cost);
        } catch (error) {
          console.error('Error updating message info:', error);
          setNextMessageCost(0);
          setCanSendFreeMessage(false);
        }
      } else {
        setNextMessageCost(0);
        setCanSendFreeMessage(true);
        setFreeMessageStatus(null);
      }
    };

    updateMessageInfo();
  }, [isPaidAgent, isAuthenticated, walletBalance, agent.id]); // Removed inputMessage dependency

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Check if user can send a message (free or paid)
  const canSendMessage = async () => {
    if (!isPaidAgent) {
      console.log('✅ [MESSAGE_CHECK] Free agent - allowing message');
      return true; // Free agent
    }
    if (!isAuthenticated) {
      console.log('❌ [MESSAGE_CHECK] Not authenticated - blocking message');
      return false; // Must be authenticated for paid agents
    }
    
    // Use cached free message status if available
    if (freeMessageStatus) {
      console.log('🆓 [MESSAGE_CHECK] Using cached free message status:', freeMessageStatus);
      
      // Can send if they have free messages OR sufficient funds for paid message
      if (freeMessageStatus.can_send_free || freeMessageStatus.free_messages_remaining > 0) {
        console.log('✅ [FREE_CHECK] User can send free message');
        return true;
      }
      
      console.log('💰 [PAID_CHECK] No free messages, checking if user can afford paid message');
      
      // Check if they have sufficient funds for a paid message
      const messageCost = nextMessageCost || 1; // Use cached cost or default to 1 cent
      console.log('💰 [PAID_CHECK] Message cost:', messageCost, 'Wallet balance:', walletBalance);
      const canAfford = walletBalance >= messageCost;
      console.log('✅ [PAID_CHECK] User can afford paid message:', canAfford);
      return canAfford;
    }
    
    console.log('❌ [MESSAGE_CHECK] No cached status available, defaulting to false');
    return false; // Default to false if no cached status
  };

  // Count user messages in current conversation
  const getUserMessageCount = () => {
    return messages.filter(msg => msg.type === 'user').length;
  };

  // Get the cost for the next message
  const getNextMessageCost = async () => {
    if (!isPaidAgent) return 0;
    if (!isAuthenticated) return 0;
    
    // Return cached cost if available
    if (nextMessageCost > 0) {
      console.log('💰 [COST_CALC] Using cached message cost:', nextMessageCost);
      return nextMessageCost;
    }
    
    try {
      const response = await fetch(`/api/wallet/message-cost/${agent.id}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: inputMessage || 'test' })
      });
      if (response.ok) {
        const cost = await response.json();
        console.log('💰 [COST_CALC] Message cost calculated:', cost);
        return cost.cost_cents;
      } else {
        console.error('❌ [COST_CALC] Failed to calculate cost:', response.status);
      }
    } catch (error) {
      console.error('❌ [COST_CALC] Error calculating message cost:', error);
    }
    
    return 1; // Default to 1 cent for paid agents
  };

  // Handle owner engagement request
  const handleRequestOwnerEngagement = async () => {
    if (!conversationId || !agentPricing?.owner_engagement_fee_cents || isRequestingEngagement) {
      return;
    }

    setIsRequestingEngagement(true);
    try {
      console.log(`🔍 [CHAT_INTERFACE] Requesting owner engagement for conversation ${conversationId}`);
      const response = await fetch('/api/wallet/request-owner-engagement', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId: conversationId,
          agentId: agent.id
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ [CHAT_INTERFACE] Owner engagement requested successfully:`, result);
        
        // Update conversation state
        setConversationState(prev => ({
          ...prev,
          owner_response_state: 'requested',
          engagement_fee_cents: result.engagement_fee_cents,
          requested_at: result.requested_at
        }));
        
        // Refresh wallet balance
        if (window.refreshWalletBalance) {
          window.refreshWalletBalance();
        }
      } else {
        const errorData = await response.json();
        console.error(`❌ [CHAT_INTERFACE] Failed to request owner engagement:`, errorData);
        alert(`Failed to request owner engagement: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error requesting owner engagement:', error);
      alert('Error requesting owner engagement. Please try again.');
    } finally {
      setIsRequestingEngagement(false);
    }
  };

  // Handle rescinding owner engagement request
  const handleRescindOwnerEngagement = async () => {
    if (!conversationId || isRequestingEngagement) {
      return;
    }

    setIsRequestingEngagement(true);
    try {
      console.log(`🔍 [CHAT_INTERFACE] Rescinding owner engagement for conversation ${conversationId}`);
      const response = await fetch('/api/wallet/rescind-owner-engagement', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId: conversationId,
          agentId: agent.id
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`✅ [CHAT_INTERFACE] Owner engagement rescinded successfully:`, result);
        
        // Update conversation state back to not_requested
        setConversationState(prev => ({
          ...prev,
          owner_response_state: 'not_requested',
          engagement_fee_cents: 0,
          requested_at: null
        }));
        
        // Refresh wallet balance (refund should be processed)
        if (window.refreshWalletBalance) {
          window.refreshWalletBalance();
        }
      } else {
        const errorData = await response.json();
        console.error(`❌ [CHAT_INTERFACE] Failed to rescind owner engagement:`, errorData);
        alert(`Failed to rescind owner engagement: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error rescinding owner engagement:', error);
      alert('Error rescinding owner engagement. Please try again.');
    } finally {
      setIsRequestingEngagement(false);
    }
  };

  // Check if owner engagement button should be enabled
  const canRequestOwnerEngagement = () => {
    if (!isAuthenticated || !conversationId || !agentPricing?.owner_engagement_fee_cents) {
      return false;
    }
    
    // Check if already fulfilled (can't request or rescind)
    if (conversationState?.owner_response_state === 'fulfilled') {
      return false;
    }
    
    // If already requested, allow rescinding (button enabled)
    if (conversationState?.owner_response_state === 'requested') {
      return true;
    }
    
    // Check if user has sufficient funds for requesting
    return walletBalance >= agentPricing.owner_engagement_fee_cents;
  };

  // Get button text based on state
  const getOwnerEngagementButtonText = () => {
    if (!conversationState) {
      return `Request Owner Review ($${(agentPricing?.owner_engagement_fee_cents / 100).toFixed(2)})`;
    }
    
    switch (conversationState.owner_response_state) {
      case 'requested':
        return 'Rescind Request';
      case 'fulfilled':
        return 'Owner Review Completed';
      default:
        return `Request Owner Review ($${(agentPricing?.owner_engagement_fee_cents / 100).toFixed(2)})`;
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || isSendingMessage) return;

    // Check if user can send message (free or paid)
    const canSend = await canSendMessage();
    if (!canSend) {
      setShowPaymentModal(true);
      return;
    }

    setIsSendingMessage(true);

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    console.log('Creating user message:', userMessage);
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Call the public agent chat API
        // Get auth token from localStorage
        const token = localStorage.getItem('token');
        console.log('Token for API call:', token ? 'present' : 'missing');
        const headers = {
          'Content-Type': 'application/json'
        };
        
        // Add Authorization header if token exists
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          console.log('Added Authorization header');
        } else {
          console.log('No token, proceeding as anonymous user');
        }

        const response = await fetch('/api/agents/public/chat', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            agentId: agent.id,
            message: inputMessage.trim(),
            conversationId: conversationId,
            conversationHistory: messages.slice(-10).map(m => ({
              role: m.type === 'user' ? 'user' : 'assistant',
              content: m.content
            }))
          })
        });

      if (response.ok) {
        const data = await response.json();
        console.log('API response:', data);
        
        // Handle insufficient funds error
        if (data.error === 'Insufficient funds') {
          const errorMessage = {
            id: Date.now() + 1,
            type: 'error',
            content: data.message || 'Insufficient funds. Please add credits to your wallet.',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, errorMessage]);
          setShowPaymentModal(true);
          return;
        }
        
        // Store conversation ID if this is a new conversation
        if (data.conversationId && !conversationId) {
          console.log('New conversation created:', data.conversationId);
          setConversationId(data.conversationId);
          // Dispatch event to refresh sidebar
          window.dispatchEvent(new CustomEvent('conversationCreated'));
          
          // Wait for the conversation to be available in the public API before updating URL
          const waitForConversationAndUpdateUrl = async () => {
            const maxRetries = 10;
            const retryDelay = 2000; // 2 seconds between retries
            
            // Wait 3 seconds before starting to check (conversation needs time to propagate)
            console.log('⏳ PublicChatInterface: Waiting 3 seconds before checking conversation availability...');
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            for (let i = 0; i < maxRetries; i++) {
              try {
                console.log(`🔄 PublicChatInterface: Checking if conversation ${data.conversationId} is available (attempt ${i + 1}/${maxRetries})`);
                const checkResponse = await fetch(`/api/agents/public/conversations/${data.conversationId}`);
                
                if (checkResponse.ok) {
                  console.log('✅ PublicChatInterface: Conversation is available, updating URL');
                  // Update URL to include the conversation ID
                  if (onUpdateUrl) {
                    onUpdateUrl({ chat: data.conversationId.toString() });
                    console.log('✅ PublicChatInterface: URL update called successfully');
                  } else {
                    console.log('⚠️ PublicChatInterface: onUpdateUrl not provided, using fallback event');
                    // Fallback to event-based approach
                    window.dispatchEvent(new CustomEvent('updateChatUrl', { 
                      detail: { conversationId: data.conversationId } 
                    }));
                  }
                  return; // Success, exit the retry loop
                } else {
                  console.log(`⏳ PublicChatInterface: Conversation not ready yet (status: ${checkResponse.status}), retrying...`);
                }
              } catch (error) {
                console.log(`❌ PublicChatInterface: Error checking conversation availability:`, error);
              }
              
              // Wait before next retry
              if (i < maxRetries - 1) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
              }
            }
            
            console.log('⚠️ PublicChatInterface: Max retries reached, updating URL anyway');
            // Update URL even if conversation isn't ready (fallback)
            if (onUpdateUrl) {
              onUpdateUrl({ chat: data.conversationId.toString() });
            } else {
              window.dispatchEvent(new CustomEvent('updateChatUrl', { 
                detail: { conversationId: data.conversationId } 
              }));
            }
          };
          
          waitForConversationAndUpdateUrl();
        }
        
        const agentResponse = {
          id: Date.now() + 1,
          type: 'assistant',
          content: data.response,
          timestamp: new Date()
        };
        console.log('Creating agent response:', agentResponse);
        setMessages(prev => [...prev, agentResponse]);
        
        // Log message charging info if available
        if (data.messageInfo) {
          console.log('Message charging info:', data.messageInfo);
          if (data.messageInfo.isFreeMessage) {
            console.log('🆓 Message was free');
          } else {
            console.log(`💰 Message cost: $${data.messageInfo.costDollars}`);
          }
        }
        
        // Refresh wallet balance if user is authenticated
        if (isAuthenticated) {
          console.log('💰 Refreshing wallet balance after message send');
          await refreshWalletBalance();
        }
      } else {
        const errorData = await response.json();
        console.error('API error:', errorData);
        throw new Error(errorData.error || 'Failed to get response from agent');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setIsSendingMessage(false);
    }
  };


  if (!agent) {
    return (
      <div className="public-chat-interface">
        <div className="no-agent-selected">
          <Bot size={48} />
          <h3>No Agent Selected</h3>
          <p>Choose an agent from the discover page to start chatting</p>
        </div>
      </div>
    );
  }

  // Debug: Log current avatar states
  console.log('Current avatar states:', {
    userAgentAvatar: userAgentAvatar ? `A has avatar: ${userAgentAvatar.substring(0, 50)}...` : 'A no avatar',
    agentAvatar: agentAvatar ? `B has avatar: ${agentAvatar.substring(0, 50)}...` : 'B no avatar'
  });

  return (
    <div className="public-chat-interface">
      <div className="messages-list">
        {/* Character.ai-style Agent Header - scrolls with messages */}
        <div className="agent-header-featured">
          <div className="agent-avatar-large">
            {agentAvatar ? (
              <img src={agentAvatar} alt={agent.name} />
            ) : (
              <Bot size={48} />
            )}
          </div>
          <div className="agent-info-featured">
            <h2 className="agent-name-featured">{agent.name}</h2>
            <p className="agent-description-featured">{agent.description}</p>
            {isPaidAgent && (
              <div className="agent-pricing-info">
                <DollarSign size={16} />
                <span className="price-display">
                  $0.01 per message after 2 free messages
                </span>
                    {freeMessageStatus && freeMessageStatus.free_messages_remaining > 0 && (
                      <span className="free-messages">
                        ({freeMessageStatus.free_messages_remaining} free messages remaining)
                      </span>
                    )}
              </div>
            )}
          </div>
        </div>
        
        {messages.map((message, index) => {
          const isUser = message.type === 'user';
          const isOwnerResponse = message.isOwnerResponse;
          const isError = message.type === 'error';
          
          console.log(`Message ${index}:`, {
            type: message.type,
            isUser,
            isOwnerResponse,
            isError,
            userAgentAvatar: userAgentAvatar ? 'has avatar' : 'no avatar',
            agentAvatar: agentAvatar ? 'has avatar' : 'no avatar',
            willShowUserAvatar: isUser && userAgentAvatar,
            willShowAgentAvatar: (isOwnerResponse || (!isUser && !isError)) && agentAvatar,
            actualAvatarBeingUsed: isUser ? (userAgentAvatar ? 'USER_AVATAR' : 'GOOGLE_ICON') : (agentAvatar ? 'AGENT_AVATAR' : 'BOT_ICON')
          });
          
          return (
            <div key={index} className={`message ${isUser ? 'user' : isOwnerResponse ? 'owner-response' : isError ? 'error' : 'assistant'}`}>
              <div className="message-avatar">
                {isUser ? (
                  userAgentAvatar ? (
                    <img src={userAgentAvatar} alt="User Avatar" />
                  ) : (
                    <GoogleIcon size={16} />
                  )
                ) : isOwnerResponse ? (
                  agentAvatar ? (
                    <img src={agentAvatar} alt={agent.name} />
                  ) : (
                    <Bot size={16} />
                  )
                ) : isError ? (
                  <Bot size={16} />
                ) : (
                  <Bot size={16} />
                )}
              </div>
              <div className="message-text">
                {isOwnerResponse && (
                  <div className="owner-response-indicator">Agent Owner</div>
                )}
                {isError && (
                  <div className="error-indicator">Error</div>
                )}
                {message.content}
              </div>
            </div>
          );
        })}
        
        {isLoading && (
          <div className="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      <div className="input-area">
        {isPaidAgent && (
          <div className="pricing-info">
            <div className="wallet-balance">
              <span>Balance: ${(walletBalance / 100).toFixed(2)}</span>
            </div>
            {agentPricing?.owner_engagement_fee_cents > 0 && (
              <button 
                className={`owner-engagement-button ${!canRequestOwnerEngagement() ? 'disabled' : ''}`}
                onClick={conversationState?.owner_response_state === 'requested' ? handleRescindOwnerEngagement : handleRequestOwnerEngagement}
                disabled={!canRequestOwnerEngagement() || isRequestingEngagement}
              >
                {isRequestingEngagement ? (conversationState?.owner_response_state === 'requested' ? 'Rescinding...' : 'Requesting...') : getOwnerEngagementButtonText()}
              </button>
            )}
          </div>
        )}
        <div className="input-container">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
                  placeholder="Ask anything..."
            className="message-input"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading || isSendingMessage}
            className="send-button"
          >
            <Send size={16} />
          </button>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="payment-modal-overlay">
          <div className="payment-modal">
            <h3>Add Credits to Continue</h3>
            <p>This agent charges $0.01 per message after the first 2 free messages.</p>
            <div className="wallet-info">
              <p>Current balance: ${(walletBalance / 100).toFixed(2)}</p>
              <p>Estimated cost: ${(nextMessageCost / 100).toFixed(2)} per message</p>
            </div>
            <div className="modal-actions">
              
              <button 
                className="cancel-button"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicChatInterface;